/*
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
*/

import { ref } from "vue";

/**
 * Composable for block resizing logic
 * Handles resize operations with drag handles (right, bottom, corner)
 */
export function useResize(gridSettings, blocks) {
  const resizingBlock = ref(null);
  const resizeDirection = ref(null);
  const resizeStartX = ref(0);
  const resizeStartY = ref(0);
  const resizeStartPosition = ref(null);

  /**
   * Start resize operation
   */
  const startResize = (event, block, direction) => {
    event.stopPropagation();
    event.preventDefault();
    resizingBlock.value = block;
    resizeDirection.value = direction;
    resizeStartX.value = event.clientX;
    resizeStartY.value = event.clientY;
    resizeStartPosition.value = { ...block.gridPosition };
  };

  /**
   * Handle mouse move during resize
   */
  const onResizeMove = (event) => {
    if (!resizingBlock.value) {
      return;
    }

    const deltaX = event.clientX - resizeStartX.value;
    const deltaY = event.clientY - resizeStartY.value;

    const cellWidth = 100; // Approximate, will vary
    const cellHeight = 100;

    const colDelta = Math.round(deltaX / cellWidth);
    const rowDelta = Math.round(deltaY / cellHeight);

    const block = blocks.value.find((b) => b.id === resizingBlock.value.id);
    if (!block) {
      return;
    }

    if (
      resizeDirection.value === "right" ||
      resizeDirection.value === "corner"
    ) {
      const newColEnd = Math.max(
        resizeStartPosition.value.colStart + 1,
        Math.min(
          resizeStartPosition.value.colEnd + colDelta,
          gridSettings.value.columns + 1,
        ),
      );
      block.gridPosition.colEnd = newColEnd;
    }

    if (
      resizeDirection.value === "bottom" ||
      resizeDirection.value === "corner"
    ) {
      const newRowEnd = Math.max(
        resizeStartPosition.value.rowStart + 1,
        Math.min(
          resizeStartPosition.value.rowEnd + rowDelta,
          gridSettings.value.rows + 1,
        ),
      );
      block.gridPosition.rowEnd = newRowEnd;
    }
  };

  /**
   * End resize operation
   */
  const onResizeEnd = (emitUpdate) => {
    if (resizingBlock.value) {
      if (emitUpdate) {
        emitUpdate();
      }
      resizingBlock.value = null;
      resizeDirection.value = null;
    }
  };

  return {
    resizingBlock,
    resizeDirection,
    startResize,
    onResizeMove,
    onResizeEnd,
  };
}
